import { FC } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';

import {
  MailSequenceReferenceDateType,
  MailSequenceScheduledSendStatus,
  MailSequenceTemplateDataFragment,
} from '../../../../generated/graphql';

import ActionButton from '../../../components/ActionButton';
import ActionButtonGroup from '../../../components/ActionButtonGroup';
import { referenceDateLabel } from '../../../lib/mailSequence';
import { getShortDate } from '../../../lib/time';
import { formatMailScheduleSummary } from '../../ScheduleMail/anchorDates';
import MailStepBadge from '../../../components/MailStepBadge';
import ScheduledSendCard from '../../PatientProfile/Sequences/ScheduledSendCard';

const stepCircleClass =
  'inline-flex h-4 w-4 shrink-0 items-center justify-center self-center rounded-full bg-secondary-100 text-[10px] font-bold leading-none text-white';

interface Props {
  template: MailSequenceTemplateDataFragment;
  onEdit: () => void;
  onDelete: () => void;
}

const TemplateCard: FC<Props> = ({ template, onEdit, onDelete }) => {
  const steps = [...template.steps].sort((a, b) => a.sortOrder - b.sortOrder);
  const firstStep = 0;
  const lastStep = Math.max(steps.length - 1, 0);
  const isSingleStep = steps.length <= 1;

  const mailRange = isSingleStep ? (
    <MailStepBadge stepIndex={firstStep} />
  ) : (
    <span className="inline-flex items-baseline gap-1 font-medium">
      <span>Mail</span>
      <span className={stepCircleClass} aria-label={`Mail ${firstStep + 1}`}>
        {firstStep + 1}
      </span>
      <span className="font-normal">to</span>
      <span className={stepCircleClass} aria-label={`Mail ${lastStep + 1}`}>
        {lastStep + 1}
      </span>
    </span>
  );

  const hasProgramDate =
    template.referenceDateType ===
      MailSequenceReferenceDateType.ProgramStartDate ||
    template.referenceDateType === MailSequenceReferenceDateType.ProgramEndDate;

  const timingClause = hasProgramDate
    ? `is scheduled relative to each client's ${referenceDateLabel(
        template.referenceDateType!,
      ).toLowerCase()}`
    : 'is scheduled relative to a date chosen when enrolling';

  const anchorLabel = hasProgramDate
    ? referenceDateLabel(template.referenceDateType!).toLowerCase()
    : 'chosen date';

  const getActivityCount = (stepIndex: number): number => {
    const step = steps[stepIndex];
    if (!step) return 0;
    if (step.hasActivityOverride) return step.activities.length;
    return template.defaultActivities.length;
  };

  const stopCardClick = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  };

  return (
    <div
      className="cursor-pointer rounded-lg border border-neutral-75 bg-white p-5 shadow-100 hover:bg-neutral-25/40"
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <div className="mb-4 flex w-full flex-row items-start justify-between gap-x-6">
        <div className="flex min-w-0 flex-1 flex-col gap-y-1">
          <span className="min-w-0 text-body font-medium text-secondary-150">
            {template.name}
          </span>
          <span className="text-small-caption text-neutral-110">
            Created {getShortDate(template.createdAt)}
            {template.updatedAt !== template.createdAt
              ? ` | Updated: ${getShortDate(template.updatedAt)}`
              : ''}
          </span>
        </div>
        <div onClick={stopCardClick} onKeyDown={stopCardClick}>
          <ActionButtonGroup size="small" className="shrink-0">
            <ActionButton
              size="small"
              aria-label="Edit template"
              title="Edit template"
              onClick={onEdit}
            >
              <PencilIcon className="h-4 w-4 text-neutral-125" />
            </ActionButton>
            <ActionButton
              size="small"
              aria-label="Delete template"
              title="Delete template"
              onClick={onDelete}
            >
              <TrashIcon className="h-4 w-4 text-red-100" />
            </ActionButton>
          </ActionButtonGroup>
        </div>
      </div>

      <p className="text-caption text-neutral-125">
        {mailRange} {timingClause}.
      </p>

      <div className="mt-6">
        <div className="-mx-1 flex snap-x snap-mandatory items-stretch gap-6 overflow-x-auto overscroll-x-contain px-3 pb-5 pt-3">
          {steps.map((step, i) => {
            const scheduleSummary = formatMailScheduleSummary(
              step.offsetDays,
              anchorLabel,
              null,
            );

            return (
              <div key={step.id} className="flex shrink-0 self-stretch">
                <ScheduledSendCard
                  interactive={false}
                  hideStatus
                  send={{
                    stepIndex: i,
                    subject: step.subject,
                    providerNote: step.providerNote,
                    status: MailSequenceScheduledSendStatus.Pending,
                    activityCount: getActivityCount(i),
                  }}
                  captionSuffix={scheduleSummary}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
